<template>
  <div class="d-flex flex-column">
    <ComparacionComisionSimulador
      v-if="puedoVerComision"
      ref="simuladorRef"
      :tipo-suministro="tipoSuministro"
      class="px-2"
      @recalcular="calcularSimulacionComisiones"
    />
    <div class="grid-header">
      <span>{{ $t("comparativa.grid.texto_informativo") }}</span>
    </div>
    <DxDataGrid v-bind="getDefaultGridProperties()" :data-source="gridSource" ref="grid"
      class="dx-card wide-card grid-comparaciones"
      :paging="{ pageSize: 5 }"
      :remote-operations="false"
      :row-auto-height="true"
      @selectionChanged="handleSelectionChanged">
      <DxSelection
        select-all-mode="allPages"
        show-check-boxes-mode="always"
        :selectedRowKeys="tarifasSeleccionadas"
        mode="multiple"
      />
      <DxColumn data-field="ImagenB64" :caption="$t('comparativa.grid.imagen')" cell-template="imagenTemplate"
        :allow-filtering="false"></DxColumn>
      <DxColumn data-field="Nombre" :caption="$t('comparativa.grid.nombre_tarifa')" data-type="string"></DxColumn>
      <DxColumn data-field="TotalAPagar" :caption="$t('liquidaciones.grid.total_a_pagar')" data-type="number">
        <DxFormat v-bind="currencyFormat()"></DxFormat>
      </DxColumn>
      <DxColumn data-field="AhorroCuantia" :caption="$t('liquidaciones.grid.total_ahorro')" data-type="number">
        <DxFormat v-bind="currencyFormat()"></DxFormat>
      </DxColumn>
      <DxColumn data-field="AhorroPorcentaje" :caption="$t('liquidaciones.grid.porcentaje_ahorro')" data-type="number">
        <DxFormat v-bind="percentFormat()"></DxFormat>
      </DxColumn>
      <DxColumn
        v-if="puedoVerComision"
        data-field="ComisionTotal"
        :caption="$t('comparativa.detalle.comision')"
        data-type="number"
        :calculate-cell-value="calcularComisionCelda"
      >
        <DxFormat v-bind="currencyFormat()"></DxFormat>
      </DxColumn>
      <template #imagenTemplate="{ data }">
        <img
            v-if="data.value"
            :src="data.value"
            style="width: 100px; height: 35px;"
            alt="Imagen"
        />
       </template>
      <DxColumnFixing :enabled="true" />
    </DxDataGrid>
  </div>
</template>

<script setup lang="ts">
import { messageBoxResult } from "@components/dialogs/message-box";
import { useModalBuilder, type ToolbarReference } from "@helpers/modal-building-helper";
import { getDefaultGridProperties } from "@helpers/grid-helper";
import { type DxButton } from "devextreme-vue";
import { DxDataGrid, DxColumn, DxColumnFixing, DxFormat, DxSelection } from "devextreme-vue/data-grid";
import { computed, getCurrentInstance, nextTick, onMounted, ref } from "vue";
import { useIntl } from "@services/i18n";
import { useApi } from "@api/integration";
import CustomStore from "devextreme/data/custom_store";
import { currencyFormat, percentFormat } from "@helpers/grid-helper";
import { useRouter } from "@/.shared/services/routing-services";
import { primaryButtonProps } from "@/.shared/helpers/button-helper";
import { toastError } from "@/.shared/services/notification-service";
import DocumentoVisor from "@/.shared/components/DocumentViewer.vue";
import { useModalService } from "@/.shared/services/modal-service";
import { useGrupoUsuarioChecker } from "@/.shared/services/auth/authorization/grupo-usuario-checker";
import { useAuthorization } from "@/.shared/services/auth/authorization";
import { PermissionsApp } from "@/.shared/services/auth/authorization/authorization-service-impl";
import ComparacionComisionSimulador from "@/components/comparaciones/ComparacionComisionSimulador.vue";
import { type DetailComparacionEnergiaOfertaModel, TipoSuministroTramiteEnergiaEnum } from "@api/api";

const api = useApi();
const intl = useIntl();
const router = useRouter();
const autho = useAuthorization();
const props = defineProps<{
  idComparacion: string;
  tipoSuministro?: TipoSuministroTramiteEnergiaEnum | null;
}>();

const grid = ref<DxDataGrid>();
const gridSource = ref<CustomStore>();
const tarifasSeleccionadas = ref<Array<number>>([]);
const modal = useModalService();
const modalBuilder = useModalBuilder();
const closeResult = ref(messageBoxResult.cancel);
const grupoUsuarioChecker = useGrupoUsuarioChecker();
const simuladorRef = ref<InstanceType<typeof ComparacionComisionSimulador> | null>(null);
const idsOfertaCargados = ref<string[]>([]);
const comisionesPorOferta = ref<Record<string, number | null>>({});

const puedoVerComision = computed(() => autho.hasPermission(PermissionsApp.ViewDatosEconomicosOperaciones));

onMounted(async () => {
  modalBuilder.setContext(getCurrentInstance()!);
  configureButtons(modalBuilder);
  setDataSource();
});

const setDataSource = () => {
  gridSource.value = new CustomStore({
    key: "Id",
    load: async () => {
      const ofertas = await api.callStandardApi("getDetailComparacionEnergiaById", { showLoading: false }, props.idComparacion);
      idsOfertaCargados.value = ofertas.map((o: DetailComparacionEnergiaOfertaModel) => o.Id);

      if (puedoVerComision.value) {
        await calcularSimulacionComisiones();
      }

      return ofertas;
    },
  });
};

async function calcularSimulacionComisiones(): Promise<void> {
  if (!puedoVerComision.value || idsOfertaCargados.value.length === 0) {
    return;
  }

  try {
    const valoresSimulacion = simuladorRef.value?.obtenerParametrosSimulacion();
    const response = await api.callStandardApi("simularComisionesComparacionEnergia", { showLoading: true },
      props.idComparacion,
      {
        IdsOferta: idsOfertaCargados.value,
        Parametros: valoresSimulacion ?? null,
      },
    );
    simuladorRef.value?.establecerParametrosSimulacion(response.ParametrosAplicados);

    const diccionarioOfertasConComision: Record<string, number | null> = {};
    for (const item of response.Comisiones ?? []) {
      diccionarioOfertasConComision[item.IdOferta] = item.ComisionTotal ?? null;
    }
    comisionesPorOferta.value = diccionarioOfertasConComision;
    grid.value?.instance?.repaint();
  }
  catch {
    toastError(intl.i18n.t("comparativa.detalle.simulador.error"));
  }
}

function calcularComisionCelda(rowData: DetailComparacionEnergiaOfertaModel & { ComisionTotal?: number | null }): number | null | undefined {
  if (!puedoVerComision.value) {
    return undefined;
  }

  const comision = comisionesPorOferta.value[rowData.Id];
  return comision ?? null;
}

function configureButtons(modal: ReturnType<typeof useModalBuilder>) {
  let generatePdfButton: ToolbarReference<DxButton>;
  let nuevaVentaButton: ToolbarReference<DxButton>;
  let cancelButton: ToolbarReference<DxButton>;
  let titulo_generar_pdf = `${intl.i18n.t("comparativa.detalle.generar_pdf")}`;
  generatePdfButton = modal.addButton("generate-pdf", { ...primaryButtonProps(titulo_generar_pdf),
    type: "default", icon: "fa-thin fa-file-pdf"}, { toolbar: "bottom", location: "after" });

  if (grupoUsuarioChecker.esColaborador()) {
    nuevaVentaButton = modal.addButton("nueva-venta", { ...primaryButtonProps(intl.i18n.t("comparativa.detalle.nueva_venta")),
        type: "default", icon: "fa-thin fa-plus"}, { toolbar: "bottom", location: "after" });
      nuevaVentaButton.update(w => w.onClick = async () => {
        await router.push(intl.i18Route({ name: "nueva-venta", query: { idComparacion: props.idComparacion}}));
            modal.close(messageBoxResult.ok);
        }
      );
  }

  cancelButton = modal.standardButtons.addClose();

  generatePdfButton.update(w => w.text = titulo_generar_pdf);
  generatePdfButton.update(w => w.onClick = () => generatePdf());
  cancelButton.update(w => w.onClick = () => modal.close(closeResult.value));
}

const handleSelectionChanged = (e: any) => {
  const selectedRows = e.selectedRowsData;
  tarifasSeleccionadas.value = selectedRows.map((row: any) => row.IdEsave);
};

const generatePdf = async () => {
  if (tarifasSeleccionadas.value.length < 1 || tarifasSeleccionadas.value.length > 3) {
    toastError(intl.i18n.t("comparativa.detalle.seleccione_entre_1_y_3"));
    return;
  }

  try {
    var ruta = await api.callStandardApi("generateComparacionEnergiaPdf", { showLoading: true }, props.idComparacion, tarifasSeleccionadas.value);
    closeResult.value = "pdf-generated";

    var tempUrl = await api.callStandardApi("documentosGenerateTempUrl", {showLoading: false}, ruta);
    var file = ruta.split("/").pop();
    let title = `${file}`;
    await modal.showModal( DocumentoVisor, { ruta: tempUrl, botones: [{
        id: "nueva-venta",
        widgetOptions: {
            type: "default",
            icon: "fa-thin fa-plus",
            text: intl.i18n.t("comparativa.detalle.descargar_pdf"),
            onClick: (closeFn: () => void) => {
                nuevaVenta(props.idComparacion);
                closeFn();
                modalBuilder.close();
            }
        },
        itemOptions: { toolbar: "bottom", location: "after" }
    }]  }, { title: title });

  } catch (e: any) {
    return false;
  }

  return true;
};

const nuevaVenta = async (id: string) => {
  await router.push(intl.i18Route({ name: "nueva-venta", query: { idComparacion: id}}));
};

</script>

<style lang="scss" scoped>


:deep(.dx-datagrid .dx-row>td) {
    padding: 0px 16px !important;
    height: 50px !important;
    line-height: 13px;
}

</style>